import { ClipFunc } from 'smg/customizer';
import * as models from 'smg/customer-api/models';

// The shape/size LIST, physical dimensions, and prices come from the system at runtime via
// GetSizes() — exactly like the coin builder. The DB does NOT carry rendering geometry (mask
// shape, on-screen canvas px, print crop, DPI); that is presentation and lives here.
//
// Keys are the literal `lineitemtypesizes`.`Type` strings for the "Made in USA - Rush Pin"
// blanks we offer (verified against the DB): Circle / Square / Diamond / Rectangle.

export type MaskKind = 'circle' | 'rect';

export interface ShapeGeometry {
    maskKind: MaskKind;
    // Presentation rotation in degrees. The 2" "Diamond" blank is a square design area shown
    // rotated 45°; the print/mask area itself is square.
    rotationDeg?: number;
}

// On-screen working resolution. Keeps the live Konva canvas to a sane pixel size (a 1.75" pin
// is 1050px here) while PRINT_DPI drives the exported file resolution via exportScale.
export const WORKING_DPI = 600;
// Embedded DPI for print exports (PNG pHYs / JPEG JFIF), matching the coin builder.
export const PRINT_DPI = 1200;

const SHAPE_GEOMETRY: Record<string, ShapeGeometry> = {
    'Circle': { maskKind: 'circle' },
    'Square': { maskKind: 'rect' },
    'Diamond': { maskKind: 'rect', rotationDeg: 45 },
    'Rectangle': { maskKind: 'rect' },
};

const DEFAULT_GEOMETRY: ShapeGeometry = { maskKind: 'rect' };

// The shape Types this builder offers, in display order. Acts as the allowlist that keeps the
// DB's novelty rush-pin blanks (All About:/Depot:/LPP:/Signature: …) out of the shape selector.
export const SHAPE_TYPES = Object.keys(SHAPE_GEOMETRY);

export function GeometryForType(type: string): ShapeGeometry {
    return SHAPE_GEOMETRY[type] ?? DEFAULT_GEOMETRY;
}

// Circle/Square/Diamond rows carry Height 0 in the DB (square aspect); fall back to width.
function dimensionsIn(size: models.LineItemTypeSize): { widthIn: number; heightIn: number } {
    const widthIn = size.width.toNumber();
    const heightIn = size.height.gt(0) ? size.height.toNumber() : widthIn;
    return { widthIn, heightIn };
}

// Canvas px derived from the API-provided dimensions — no hardcoded 969. Rectangles get a
// rectangular canvas (real aspect ratio); circle/square/diamond get a square one.
export function CanvasSize(sizeInit: models.ILineItemTypeSize, dpi: number = WORKING_DPI): { width: number; height: number } {
    const { widthIn, heightIn } = dimensionsIn(new models.LineItemTypeSize(sizeInit));
    return {
        width: Math.round(widthIn * dpi),
        height: Math.round(heightIn * dpi),
    };
}

// pt→px for text rendered on the pin canvas. There are 72 points per inch, and the canvas is
// `inches × dpi` px (see CanvasSize), so px-per-inch is exactly the DPI — no per-size literal
// like the coin builder's baked `969 / 2`. Replaces that hardcode for the pin code paths.
export function PtToPx(pt: number, dpi: number = WORKING_DPI): number {
    return Math.round(pt * (dpi / 72));
}

// The physical blanks' corners/vertices are slightly rounded (~0.01" measured off the
// renders' alpha masks) — the printable area rounds to match so a sharp art corner never
// sits against a curved metal corner.
export const CORNER_RADIUS_IN = 0.01;

export function CornerRadiusPx(dpi: number = WORKING_DPI): number {
    return Math.round(CORNER_RADIUS_IN * dpi);
}

// Print bleed per manufacturing: files extend 0.05" total per axis past the cut line
// (a 1" pin's print files are 26.67mm) so trim drift never exposes bare metal
export const BLEED_IN = 0.05;

/** Bleed per SIDE in print-output pixels */
export function PrintBleedPx(): number {
    return Math.round(BLEED_IN / 2 * PRINT_DPI);
}

// Layer clip path matching the shape's printable area — ink is ink at print time, so
// uploads and text crop to it exactly like the coin builder's circle masks. Runs to the
// blank's edge: these pins print edge-to-edge (per manufacturing); corners round to
// match the physical blanks'. The diamond's vertices sit at the canvas edge midpoints
// (see the page's shapeConfig).
export function ShapeClipFunc(sizeInit: models.ILineItemTypeSize): ClipFunc {
    const size = new models.LineItemTypeSize(sizeInit);
    const { width, height } = CanvasSize(size);
    const geometry = GeometryForType(size.type);
    if (geometry.maskKind === 'circle') {
        return ctx => {
            ctx.arc(width / 2, height / 2, width / 2, 0, Math.PI * 2, false);
        };
    }
    const corner = CornerRadiusPx();
    if (geometry.rotationDeg) {
        const vertices = [
            { x: width / 2, y: 0 },
            { x: width, y: height / 2 },
            { x: width / 2, y: height },
            { x: 0, y: height / 2 },
        ];
        return ctx => {
            ctx.moveTo((vertices[3].x + vertices[0].x) / 2, (vertices[3].y + vertices[0].y) / 2);
            for (let i = 0; i < vertices.length; i++) {
                const vertex = vertices[i];
                const next = vertices[(i + 1) % vertices.length];
                ctx.arcTo(vertex.x, vertex.y, next.x, next.y, corner);
            }
            ctx.closePath();
        };
    }
    return ctx => {
        ctx.moveTo(corner, 0);
        ctx.arcTo(width, 0, width, height, corner);
        ctx.arcTo(width, height, 0, height, corner);
        ctx.arcTo(0, height, 0, 0, corner);
        ctx.arcTo(0, 0, width, 0, corner);
        ctx.closePath();
    };
}
